import {_state} from "../../state/state.js";

const desktopMedia = window.matchMedia('(min-width: 1025px)');

export const setupHeader = () => {
    return new Promise((resolve, reject) => {
        const header = document.querySelector('#header');
        if (header) {
            headerDeskMenu(header);
            headerPanels(header);
            mobMenu(header);
            langSwitcher(header);
            toggleHeaderOnScroll(header);
            changeHeaderType(header);
        }

        resolve();
    })
}

const toggleBackdrop = (isActive) => {
    document.querySelector('#backdrop')?.classList.toggle('active', isActive);
};

/**
 * Desktop nav: hover/focus on a top-level item → it's active, the others are muted (.is-nav-hover on header).
 * Items with children (.has-panel) open their mega-menu panel and switch the header to the light state (.is-active-menu).
 * The panel stays open while the cursor is anywhere inside the header (closing happens on header mouseleave),
 * so moving from the link down into the panel never "breaks" the hover.
 */
const headerDeskMenu = (header) => {
    const items = header.querySelectorAll('.header__nav .header__menu-item');
    const lang = header.querySelector('.header__lang');
    if (!items.length) return;

    let closeTimeout;

    const closeAll = () => {
        lang?.classList.remove('is-hover');
        items.forEach((item) => {
            item.classList.remove('is-hover', 'is-open');
            item.querySelector(':scope > .header__menu-link')?.setAttribute('aria-expanded', 'false');
        });
        header.classList.remove('is-nav-hover', 'is-active-menu');
        toggleBackdrop(false);
    };

    const activate = (item) => {
        if (!desktopMedia.matches) return;
        clearTimeout(closeTimeout);

        items.forEach((otherItem) => {
            if (otherItem === item) return;
            otherItem.classList.remove('is-hover', 'is-open');
            otherItem.querySelector(':scope > .header__menu-link')?.setAttribute('aria-expanded', 'false');
        });

        lang?.classList.remove('is-hover');
        item.classList.add('is-hover');
        header.classList.add('is-nav-hover');

        const hasPanel = item.classList.contains('has-panel');
        item.classList.toggle('is-open', hasPanel);
        item.querySelector(':scope > .header__menu-link')?.setAttribute('aria-expanded', hasPanel ? 'true' : 'false');
        header.classList.toggle('is-active-menu', hasPanel);
        toggleBackdrop(hasPanel);
    };

    items.forEach((item) => {
        item.addEventListener('mouseenter', () => activate(item));
        item.addEventListener('focusin', () => activate(item));

        // Leaving a plain item (no panel) → drop the muted state right away
        item.addEventListener('mouseleave', () => {
            if (!item.classList.contains('has-panel')) {
                item.classList.remove('is-hover');
                header.classList.remove('is-nav-hover');
            }
        });
    });

    // Language switcher behaves like a plain menu item: hovering it mutes the menu (and closes an open panel)
    lang?.addEventListener('mouseenter', () => {
        if (!desktopMedia.matches) return;
        closeAll();
        lang.classList.add('is-hover');
        header.classList.add('is-nav-hover');
    });
    lang?.addEventListener('mouseleave', () => {
        lang.classList.remove('is-hover');
        header.classList.remove('is-nav-hover');
    });

    header.addEventListener('mouseleave', () => {
        closeTimeout = setTimeout(closeAll, 150);
    });
    header.addEventListener('mouseenter', () => clearTimeout(closeTimeout));

    header.addEventListener('focusout', (e) => {
        if (!header.contains(e.relatedTarget)) closeAll();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !header.classList.contains('is-active-menu')) return;
        const openLink = header.querySelector('.header__menu-item.is-open > .header__menu-link');
        closeAll();
        openLink?.focus();
    });

    document.querySelector('#backdrop')?.addEventListener('click', closeAll);
    desktopMedia.addEventListener('change', closeAll);
};

/**
 * Mega-menu panel: hover/focus on a page → it's active (underline + arrow), siblings muted,
 * matching image + marquee title shown. The last hovered image stays when the cursor leaves the list.
 */
const MARQUEE_SPEED = 40; // px per second — lower = slower

// Same speed for short and long titles: duration = width of one half / speed
const setMarqueeDuration = (header) => {
    header.querySelectorAll('.header__panel-title-track').forEach((track) => {
        const group = track.querySelector('.header__panel-title-group');
        if (!group || !group.offsetWidth) return;
        track.style.animationDuration = `${group.offsetWidth / MARQUEE_SPEED}s`;
    });
};

const headerPanels = (header) => {
    const panels = header.querySelectorAll('.header__panel');

    // Panels are visibility:hidden but still laid out, so widths are measurable.
    // Fonts change the width → recalc after they load and on resize.
    setMarqueeDuration(header);
    document.fonts?.ready.then(() => setMarqueeDuration(header));
    window.addEventListener('resize', () => setMarqueeDuration(header));

    panels.forEach((panel) => {
        const list = panel.querySelector('.header__panel-list');
        const pageItems = panel.querySelectorAll('.header__panel-item');
        const images = panel.querySelectorAll('.header__panel-image');
        const titlesWrapper = panel.querySelector('.header__panel-titles');
        const titles = panel.querySelectorAll('.header__panel-title');
        if (!list || !pageItems.length) return;

        const setActive = (index) => {
            const setIndex = (els) => els.forEach((el) => el.classList.toggle('is-active', el.dataset.index === index));
            setIndex(pageItems);
            setIndex(titles);

            // Keep the current image if the hovered page has none
            const image = panel.querySelector(`.header__panel-image[data-index="${index}"]`);
            if (image && image.querySelector('img')) setIndex(images);

            list.classList.add('is-hovering');
            titlesWrapper?.classList.add('is-visible');
        };

        const reset = () => {
            list.classList.remove('is-hovering');
            titlesWrapper?.classList.remove('is-visible');
        };

        pageItems.forEach((pageItem) => {
            pageItem.addEventListener('mouseenter', () => setActive(pageItem.dataset.index));
            pageItem.addEventListener('focusin', () => setActive(pageItem.dataset.index));
        });

        list.addEventListener('mouseleave', reset);
        list.addEventListener('focusout', (e) => {
            if (!list.contains(e.relatedTarget)) reset();
        });
    });
};

/**
 * Mobile menu: "MENU" button toggles the full-screen panel (clip-path reveal on .active, see _mob-menu.scss).
 * Parent rows are an accordion — several can be open at once (as in the design).
 */
const mobMenu = (header) => {
    const burgerBtn = header.querySelector('#header-burger');
    const menu = header.querySelector('#header-burger-menu');
    if (!burgerBtn || !menu) return;

    let isOpen = false;

    const toggle = (open) => {
        isOpen = open;

        burgerBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        menu.setAttribute('aria-hidden', open ? 'false' : 'true');
        menu.classList.toggle('active', open);
        header.classList.toggle('active-mob-menu', open);
        document.documentElement.classList.toggle('overflow-hidden', open);
        document.body.classList.toggle('overflow-hidden', open);

        if (open) {
            _state.lenisInit?.stop();
        } else {
            _state.lenisInit?.start();
        }
    };

    burgerBtn.addEventListener('click', () => toggle(!isOpen));

    menu.querySelectorAll('.header__mob-toggle').forEach((btn) => {
        btn.addEventListener('click', () => {
            const item = btn.closest('.header__mob-item');
            const expanded = !item.classList.contains('is-open');
            item.classList.toggle('is-open', expanded);
            btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen) {
            toggle(false);
            burgerBtn.focus();
        }
    });

    desktopMedia.addEventListener('change', (e) => {
        if (e.matches && isOpen) toggle(false);
    });
};

/**
 * Language switcher: click toggles the list, click outside / Esc closes it.
 * Choosing a language is a normal link to the Polylang translation URL.
 */
const langSwitcher = (header) => {
    const switchers = header.querySelectorAll('.lang-switcher');

    const close = (switcher) => {
        switcher.classList.remove('is-open');
        switcher.querySelector('.lang-switcher__current')?.setAttribute('aria-expanded', 'false');
    };

    switchers.forEach((switcher) => {
        const btn = switcher.querySelector('.lang-switcher__current');
        if (!btn) return;

        btn.addEventListener('click', () => {
            const open = !switcher.classList.contains('is-open');
            switchers.forEach(close);
            switcher.classList.toggle('is-open', open);
            btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
    });

    document.addEventListener('click', (e) => {
        switchers.forEach((switcher) => {
            if (!switcher.contains(e.target)) close(switcher);
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        switchers.forEach((switcher) => {
            if (!switcher.classList.contains('is-open')) return;
            close(switcher);
            switcher.querySelector('.lang-switcher__current')?.focus();
        });
    });
};

/**
 * Hide on scroll down, show on scroll up (logic from seearia).
 * Never hides while a menu is open.
 */
const toggleHeaderOnScroll = (header) => {
    const update = () => {
        const scrollTop = window.pageYOffset || window.scrollY;
        const isMenuOpen = header.classList.contains('active-mob-menu') || header.classList.contains('is-active-menu');

        if (!isMenuOpen && _state.scrollDirection && scrollTop > header.getBoundingClientRect().height) {
            _state.updateState({isHeaderHide: true});
            header.classList.add('hide');
        } else {
            _state.updateState({isHeaderHide: false});
            header.classList.remove('hide');
        }
    };

    ['resize', 'scroll'].forEach((event) => window.addEventListener(event, update, {passive: true}));
    update();
};

/**
 * Header color version by the section under it (logic from seearia):
 * - data-header-type="blue" (or a .bg-white section) → blue header for light backgrounds
 * - data-header-type="white" / nothing → white header for dark/transparent backgrounds (default)
 * The first element with an explicit type wins, so a nested element can override its section.
 */
const changeHeaderType = (header) => {
    const getType = (el) => {
        if (el.dataset.headerType) return el.dataset.headerType;
        if (el.classList.contains('bg-white')) return 'blue';
        return null;
    };

    const update = () => {
        const sections = document.querySelectorAll('[data-header-type], section, .bg-white, .footer');
        const point = header.getBoundingClientRect().height / 2;
        let type = 'white';

        for (const section of sections) {
            const rect = section.getBoundingClientRect();
            if (point < rect.top || point > rect.bottom) continue;

            const sectionType = getType(section);
            if (sectionType) {
                type = sectionType;
                break;
            }
        }

        header.classList.toggle('is-blue', type === 'blue');
    };

    ['resize', 'scroll'].forEach((event) => window.addEventListener(event, update, {passive: true}));
    update();
};
